import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, setDoc, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Search, UserPlus, Check, X, Users, Trash2, Building2, Mail, Filter, Phone, MapPin, Clock, AlertCircle } from 'lucide-react';
import PageHeader from './common/PageHeader';

function ConnectionList({ user }) {
    // 🌏 해외 계정: 영문 표기 + 상대 연락처 비공개(우회거래 방지) + 검색 대신 Find Labs
    const isOverseasUser = user?.labKind === 'overseas';
    const [connections, setConnections] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [filterType, setFilterType] = useState('all');
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // 🔥 직원 여부 확인
    const isStaff = user?.userType === 'staff';
    const isOwner = user?.businessType === 'dental' || user?.businessType === 'lab' || user?.businessType === 'outsource';
    
    // 🔥 회사 기준 ID (회사 소속이면 companyId, 아니면 uid)
    const targetId = user?.companyId || user?.uid;

    const cleanupDone = React.useRef(false);

    useEffect(() => {
        fetchConnections();
    }, [user]);

    // 🔥 중복 pending 자동 정리 (이미 연결된 상대와의 pending 요청 삭제)
    useEffect(() => {
        const cleanupDuplicates = async () => {
            if (cleanupDone.current) return;
            
            const acceptedPartners = connections
                .filter(c => c.status === 'accepted')
                .map(c => c.partnerId);
            
            const pendingToDelete = connections.filter(
                c => c.status === 'pending' && acceptedPartners.includes(c.partnerId)
            );

            if (pendingToDelete.length > 0) {
                cleanupDone.current = true;
                for (const conn of pendingToDelete) {
                    try {
                        await deleteDoc(doc(db, 'connections', conn.id));
                        console.log('중복 pending 삭제:', conn.id);
                    } catch (e) {
                        console.error('중복 삭제 실패:', e);
                    }
                }
                fetchConnections();
            }
        };

        if (connections.length > 0) {
            cleanupDuplicates();
        }
    }, [connections]);

    // 상대 업체 users 문서 읽기 — 탈퇴·삭제된 업체는 rules(10-03 P0-1: resource.data 판정)가 get 을 permission-denied 로 막는다.
    // 한 곳이라도 실패하면 전체 목록이 비던 결함(10-12 ivanlab 거래처 0건, 연결 26건 중 삭제 업체 4곳) → 건별로 건너뛰고 기록.
    const readPartner = async (partnerId) => {
        if (!partnerId) return null;
        try {
            const snap = await getDoc(doc(db, 'users', partnerId));
            return snap.exists() ? snap : null;
        } catch (e) {
            console.warn('거래처 정보 읽기 건너뜀(탈퇴·삭제 업체 가능):', e?.code || e?.message);
            return null;
        }
    };

    const fetchConnections = async () => {
        try {
            setLoading(true);
            const connectionsRef = collection(db, 'connections');
            
            // 🔥 회사 기준으로 조회 (receiverId, recipientId 둘 다 체크)
            const sentQuery = query(connectionsRef, where('requesterId', '==', targetId));
            const receivedQuery1 = query(connectionsRef, where('receiverId', '==', targetId));
            const receivedQuery2 = query(connectionsRef, where('recipientId', '==', targetId));

            const [sentSnapshot, receivedSnapshot1, receivedSnapshot2] = await Promise.all([
                getDocs(sentQuery),
                getDocs(receivedQuery1),
                getDocs(receivedQuery2)
            ]);

            const allConnections = [];
            const addedIds = new Set(); // 중복 방지

            // 보낸 요청
            for (const docSnap of sentSnapshot.docs) {
                const data = docSnap.data();
                const partnerId = data.receiverId || data.recipientId;
                if (!partnerId || addedIds.has(docSnap.id)) continue;
                
                const partnerDoc = await readPartner(partnerId);
                if (partnerDoc) {
                    addedIds.add(docSnap.id);
                    allConnections.push({
                        id: docSnap.id,
                        ...data,
                        partnerData: partnerDoc.data(),
                        partnerId: partnerId,
                        type: 'sent'
                    });
                }
            }

            // 받은 요청 (receiverId로 조회된 것)
            for (const docSnap of receivedSnapshot1.docs) {
                if (addedIds.has(docSnap.id)) continue;
                const data = docSnap.data();
                const partnerDoc = await readPartner(data.requesterId);
                if (partnerDoc) {
                    addedIds.add(docSnap.id);
                    allConnections.push({
                        id: docSnap.id,
                        ...data,
                        partnerData: partnerDoc.data(),
                        partnerId: data.requesterId,
                        type: 'received'
                    });
                }
            }

            // 받은 요청 (recipientId로 조회된 것)
            for (const docSnap of receivedSnapshot2.docs) {
                if (addedIds.has(docSnap.id)) continue;
                const data = docSnap.data();
                const partnerDoc = await readPartner(data.requesterId);
                if (partnerDoc) {
                    addedIds.add(docSnap.id);
                    allConnections.push({
                        id: docSnap.id,
                        ...data,
                        partnerData: partnerDoc.data(),
                        partnerId: data.requesterId,
                        type: 'received'
                    });
                }
            }

            console.log("[DEBUG] 전체 연결 데이터:", allConnections);
            console.log("[DEBUG] 받은 pending 요청:", allConnections.filter(c => c.type === "received" && c.status === "pending"));
            setConnections(allConnections);
        } catch (error) {
            console.error('연결 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            alert('검색어를 입력하세요.');
            return;
        }

        try {
            setSearching(true);
            // 🔐 2026-10-03 보안 P0-1: 필터 없는 users 전체 조회는 rules 에서 막혔다(환자 문서 노출 차단).
            //   아래 클라이언트 필터와 같은 업체 유형으로 서버 쿼리를 제약한다(결과 동일).
            const usersRef = query(collection(db, 'users'), where('businessType', 'in', ['dental', 'lab', 'outsource']));
            const usersSnapshot = await getDocs(usersRef);
            
            const results = [];
            usersSnapshot.docs.forEach(docSnap => {
                const userData = docSnap.data();
                const userId = docSnap.id;
                
                // 🔥 자기 회사/자신 제외
                if (userId === targetId) return;
                if (userId === user.uid) return;

                // 🔥 직원 계정은 검색 결과에서 제외 (업체만 표시)
                if (userData.userType === 'staff') return;

                // 🔥 개인회원 제외 - 실제 업체(치과/기공소)만 표시
                if (userData.userType === 'individual') return;
                if (!userData.businessType || (userData.businessType !== 'dental' && userData.businessType !== 'lab' && userData.businessType !== 'outsource')) return;

                const email = (userData.email || '').toLowerCase();
                const queryLower = searchQuery.toLowerCase();

                if (email.includes(queryLower)) {
                    const existingConnection = connections.find(
                        conn => conn.partnerId === userId
                    );
                    
                    results.push({
                        id: userId,
                        ...userData,
                        connectionStatus: existingConnection ? existingConnection.status : null,
                        connectionId: existingConnection ? existingConnection.id : null
                    });
                }
            });

            setSearchResults(results);
        } catch (error) {
            console.error('검색 실패:', error);
            alert('검색에 실패했습니다.');
        } finally {
            setSearching(false);
        }
    };

    const handleConnect = async (partnerId) => {
        try {
            // 🔥 상대방이 이미 나에게 요청을 보냈는지 확인
            const existingRequest = connections.find(
                conn => conn.partnerId === partnerId && 
                        conn.type === 'received' && 
                        conn.status === 'pending'
            );

            if (existingRequest) {
                // 상대방이 이미 요청을 보낸 상태 → 자동 수락
                await updateDoc(doc(db, 'connections', existingRequest.id), {
                    status: 'accepted',
                    updatedAt: new Date(),
                    acceptedBy: user.uid
                });
                alert('상대방이 이미 연결 요청을 보냈습니다. 자동으로 연결되었습니다!');
            } else {
                // 새 요청 생성 — 쌍 고정 ID로 중복 원천 차단 (같은 쌍 = 같은 문서)
                const connId = [targetId, partnerId].sort().join('_');
                await setDoc(doc(db, 'connections', connId), {
                    requesterId: targetId,
                    receiverId: partnerId,
                    recipientId: partnerId,
                    status: 'pending',
                    createdAt: new Date(),
                    requesterName: user.businessName || user.companyName || user.name,
                    requesterType: user.businessType
                }, { merge: true });
                alert('연결 요청을 보냈습니다.');
            }

            handleSearch();
            fetchConnections();
        } catch (error) {
            console.error('연결 요청 실패:', error);
            alert('연결 요청에 실패했습니다.');
        }
    };

    const handleAccept = async (connectionId) => {
        try {
            const conn = connections.find(c => c.id === connectionId);
            
            await updateDoc(doc(db, 'connections', connectionId), {
                status: 'accepted',
                updatedAt: new Date(),
                acceptedBy: user.uid
            });

            // 🔥 반대 방향 요청이 있으면 삭제 (중복 방지)
            if (conn) {
                const reverseRequest = connections.find(
                    c => c.partnerId === conn.partnerId && 
                         c.type === 'sent' && 
                         c.status === 'pending'
                );
                if (reverseRequest) {
                    await deleteDoc(doc(db, 'connections', reverseRequest.id));
                }
            }

            alert('연결을 수락했습니다.');
            fetchConnections();
        } catch (error) {
            console.error('수락 실패:', error);
            alert('수락에 실패했습니다.');
        }
    };

    const handleReject = async (connectionId) => {
        try {
            await updateDoc(doc(db, 'connections', connectionId), {
                status: 'rejected',
                updatedAt: new Date(),
                rejectedBy: user.uid
            });

            alert('연결을 거부했습니다.');
            fetchConnections();
        } catch (error) {
            console.error('거부 실패:', error);
            alert('거부에 실패했습니다.');
        }
    };

    // 보낸 연결 요청 취소 (2026-08-21 대표 지시) — 수락 전 대기중 요청을 요청자가 거둬들인다.
    // rules상 요청자 delete 허용(연결해제와 동일 권한)이라 문서 삭제로 처리 — 상대 화면의 '받은 요청'에서도 사라진다.
    const handleCancelRequest = async (connectionId, partnerName) => {
        if (!window.confirm(`"${partnerName || '상대 업체'}"에 보낸 연결 요청을 취소하시겠습니까?`)) return;
        try {
            await deleteDoc(doc(db, 'connections', connectionId));
            alert('연결 요청을 취소했습니다.');
            fetchConnections();
        } catch (error) {
            console.error('요청 취소 실패:', error);
            alert('요청 취소에 실패했습니다.');
        }
    };

    const handleDisconnect = async (connectionId, partnerName) => {
        if (!window.confirm(`"${partnerName}"와의 연결을 해제하시겠습니까?\n\n해제 후에는 다시 연결 요청을 보내야 합니다.`)) {
            return;
        }

        try {
            await deleteDoc(doc(db, 'connections', connectionId));
            alert('연결이 해제되었습니다.');
            setShowDetailModal(false);
            fetchConnections();
            
            if (searchResults.length > 0) {
                handleSearch();
            }
        } catch (error) {
            console.error('연결 해제 실패:', error);
            alert('연결 해제에 실패했습니다.');
        }
    };

    const handleCardClick = (conn) => {
        setSelectedPartner(conn);
        setShowDetailModal(true);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        if (timestamp.toDate) return timestamp.toDate().toLocaleDateString('ko-KR');
        if (timestamp instanceof Date) return timestamp.toLocaleDateString('ko-KR');
        return '-';
    };

    const pendingRequests = connections.filter(
        conn => conn.type === 'received' && (conn.status === 'pending' || !conn.status)
    );
    
    // 🔥 내가 보낸 대기중인 요청
    const sentPendingRequests = connections.filter(
        conn => conn.type === 'sent' && (conn.status === 'pending' || !conn.status)
    );
    
    let connectedPartners = connections.filter(conn => conn.status === 'accepted');
    
    if (filterType !== 'all') {
        connectedPartners = connectedPartners.filter(conn => {
            return conn.partnerData?.businessType === filterType;
        });
    }

    const getTypeDisplay = (businessType) => {
        if (businessType === 'dental') return { emoji: '🏥', label: '치과', bg: '#dbeafe', color: '#2563eb' };
        if (businessType === 'lab') return { emoji: '🔧', label: '기공소', bg: '#d1fae5', color: '#059669' };
        if (businessType === 'outsource') return { emoji: '🏭', label: '외주업체', bg: '#ede9fe', color: '#7c3aed' };
        return { emoji: '🏢', label: '업체', bg: '#f1f5f9', color: '#64748b' };
    };

    const stats = {
        total: connections.filter(conn => conn.status === 'accepted').length,
        clinics: connections.filter(conn => conn.status === 'accepted' && conn.partnerData?.businessType === 'dental').length,
        labs: connections.filter(conn => conn.status === 'accepted' && conn.partnerData?.businessType === 'lab').length,
        outsource: connections.filter(conn => conn.status === 'accepted' && conn.partnerData?.businessType === 'outsource').length,
        pending: pendingRequests.length,
        sentPending: sentPendingRequests.length
    };

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner}></div>
                <p>로딩 중...</p>
            </div>
        );
    }

    return (
        <div>
            <PageHeader icon={Building2} title={isOverseasUser ? 'Partners' : '거래처 관리'} />
            {/* 🌏 해외 계정: 검색·통계 대신 안내 (탐색은 Find Labs, 연락처 비공개) */}
            {isOverseasUser && (
                <div style={{ margin: '10px 0 14px', padding: '12px 16px', background: '#EEF0FA', border: '1px solid #C7D2FE', borderRadius: 10, fontSize: 13, color: '#3730A3', lineHeight: 1.6 }}>
                    Your connected Korean labs. To find new labs, use <b>Find Labs</b>. Contact details are private — all communication happens in translated <b>Chat</b>.
                </div>
            )}
            {/* 통계 대시보드 */}
            {!isOverseasUser && (
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statIcon}>
                        <Users size={24} color="#6366f1" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{stats.total}</div>
                        <div style={styles.statLabel}>전체 연결</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#dbeafe'}}>
                        <Building2 size={24} color="#2563eb" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{stats.clinics}</div>
                        <div style={styles.statLabel}>치과</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#d1fae5'}}>
                        <Building2 size={24} color="#059669" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{stats.labs}</div>
                        <div style={styles.statLabel}>기공소</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#ede9fe'}}>
                        <Building2 size={24} color="#7c3aed" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{stats.outsource}</div>
                        <div style={styles.statLabel}>외주업체</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#fef3c7'}}>
                        <Mail size={24} color="#d97706" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{stats.pending}</div>
                        <div style={styles.statLabel}>받은 요청</div>
                    </div>
                </div>
            </div>
            )}

            {/* 검색 — 해외 계정은 Find Labs로 유도 (이메일 검색은 우회 경로) */}
            {!isOverseasUser && (
            <div style={styles.searchSection}>
                <h2 style={styles.sectionTitle}>🔍 업체 검색</h2>
                
                {/* 🔥 직원일 경우 안내 메시지 표시 */}
                {isStaff ? (
                    <div style={styles.staffNotice}>
                        <AlertCircle size={48} color="#f59e0b" />
                        <h3 style={styles.staffNoticeTitle}>직원 계정은 거래처 신청을 할 수 없습니다</h3>
                        <p style={styles.staffNoticeText}>
                            거래처 신청은 업체 오너만 가능합니다.<br />
                            회사 소속으로 등록되어 있으며, 회사의 기존 거래처를 이용할 수 있습니다.
                        </p>
                    </div>
                ) : (
                    <>
                        <div style={styles.searchBox}>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                style={styles.searchInput}
                                placeholder="이메일로 검색하세요..."
                            />
                            <button onClick={handleSearch} style={styles.searchButton} disabled={searching}>
                                <Search size={18} />
                                {searching ? '검색 중...' : '검색'}
                            </button>
                        </div>

                        {searchResults.length > 0 && (
                            <div style={styles.searchResults}>
                                <div style={styles.searchResultsHeader}>
                                    검색 결과 {searchResults.length}개
                                </div>
                                {searchResults.map((result) => (
                                    <div 
                                        key={result.id} 
                                        style={styles.resultCard}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#6366f1';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        <div style={styles.resultInfo}>
                                            <div style={styles.resultHeader}>
                                                <h3 style={styles.resultName}>{result.businessName}</h3>
                                                <span style={{
                                                    ...styles.resultType,
                                                    backgroundColor: getTypeDisplay(result.businessType).bg,
                                                    color: getTypeDisplay(result.businessType).color
                                                }}>
                                                    {getTypeDisplay(result.businessType).emoji} {getTypeDisplay(result.businessType).label}
                                                </span>
                                            </div>
                                            <div style={styles.resultDetails}>
                                                <p style={styles.resultEmail}>
                                                    <Mail size={14} />
                                                    {result.email}
                                                </p>
                                                {result.phone && (
                                                    <p style={styles.resultEmail}>
                                                        <Phone size={14} />
                                                        {result.phone}
                                                    </p>
                                                )}
                                                {result.address && (
                                                    <p style={styles.resultEmail}>
                                                        <MapPin size={14} />
                                                        {result.address}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            {!result.connectionStatus && (
                                                <button
                                                    onClick={() => handleConnect(result.id)}
                                                    style={styles.connectButton}
                                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                >
                                                    <UserPlus size={18} />
                                                    연결 요청
                                                </button>
                                            )}
                                            {result.connectionStatus === 'pending' && (
                                                <span style={styles.statusBadge}>⏳ 요청 대기중</span>
                                            )}
                                            {result.connectionStatus === 'accepted' && (
                                                <span style={styles.statusBadgeConnected}>✓ 연결됨</span>
                                            )}
                                            {result.connectionStatus === 'rejected' && (
                                                <span style={styles.statusBadgeRejected}>✗ 거부됨</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
            )}

            {/* 🔥 받은 연결 요청 (승인/거부 가능) */}
            {pendingRequests.length > 0 && (
                <div style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <h2 style={styles.sectionTitle}>
                            📩 받은 연결 요청
                        </h2>
                        <span style={styles.badge}>{pendingRequests.length}건</span>
                    </div>
                    <div style={styles.grid}>
                        {pendingRequests.map((conn) => (
                            <div 
                                key={conn.id} 
                                style={styles.requestCard}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div style={styles.cardHeader}>
                                    <h3 style={styles.cardTitle}>
                                        {conn.partnerData?.businessName || '이름 없음'}
                                    </h3>
                                    <div style={styles.requestBadge}>NEW</div>
                                </div>
                                <span style={{
                                    ...styles.cardType,
                                    backgroundColor: getTypeDisplay(conn.partnerData?.businessType).bg,
                                    color: getTypeDisplay(conn.partnerData?.businessType).color
                                }}>
                                    {getTypeDisplay(conn.partnerData?.businessType).emoji} {getTypeDisplay(conn.partnerData?.businessType).label}
                                </span>
                                <div style={styles.cardInfo}>
                                    <p style={styles.cardEmail}>
                                        <Mail size={14} />
                                        {conn.partnerData?.email}
                                    </p>
                                    {conn.partnerData?.phone && (
                                        <p style={styles.cardEmail}>
                                            <Phone size={14} />
                                            {conn.partnerData?.phone}
                                        </p>
                                    )}
                                    <p style={styles.cardDate}>
                                        <Clock size={14} />
                                        요청일: {formatDate(conn.createdAt)}
                                    </p>
                                </div>
                                <div style={styles.cardActions}>
                                    <button
                                        onClick={() => handleAccept(conn.id)}
                                        style={styles.acceptButton}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <Check size={16} />
                                        수락
                                    </button>
                                    <button
                                        onClick={() => handleReject(conn.id)}
                                        style={styles.rejectButton}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <X size={16} />
                                        거부
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 🔥 보낸 연결 요청 (대기중) */}
            {sentPendingRequests.length > 0 && (
                <div style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <h2 style={styles.sectionTitle}>
                            📤 보낸 연결 요청
                        </h2>
                        <span style={{...styles.badge, backgroundColor: '#e0e7ff', color: '#4f46e5'}}>{sentPendingRequests.length}건 대기중</span>
                    </div>
                    <div style={styles.grid}>
                        {sentPendingRequests.map((conn) => (
                            <div 
                                key={conn.id} 
                                style={{...styles.requestCard, borderColor: '#e0e7ff'}}
                            >
                                <div style={styles.cardHeader}>
                                    <h3 style={styles.cardTitle}>
                                        {conn.partnerData?.businessName || '이름 없음'}
                                    </h3>
                                    <div style={{...styles.requestBadge, backgroundColor: '#e0e7ff', color: '#4f46e5'}}>대기중</div>
                                </div>
                                <span style={{
                                    ...styles.cardType,
                                    backgroundColor: getTypeDisplay(conn.partnerData?.businessType).bg,
                                    color: getTypeDisplay(conn.partnerData?.businessType).color
                                }}>
                                    {getTypeDisplay(conn.partnerData?.businessType).emoji} {getTypeDisplay(conn.partnerData?.businessType).label}
                                </span>
                                <div style={styles.cardInfo}>
                                    <p style={styles.cardEmail}>
                                        <Mail size={14} />
                                        {conn.partnerData?.email}
                                    </p>
                                    <p style={styles.cardDate}>
                                        <Clock size={14} />
                                        요청일: {formatDate(conn.createdAt)}
                                    </p>
                                </div>
                                <div style={styles.sentPendingNote}>
                                    상대방의 승인을 기다리고 있습니다
                                </div>
                                {/* 요청 취소 (2026-08-21 대표 지시) */}
                                <button
                                    onClick={() => handleCancelRequest(conn.id, conn.partnerData?.businessName)}
                                    style={{ marginTop: 10, width: '100%', padding: '9px 0', border: '1px solid #FCA5A5', background: '#fff', color: '#DC2626', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                                >
                                    <X size={14} />
                                    요청 취소
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 연결된 업체 */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <div>
                        <h2 style={styles.sectionTitle}>
                            {isOverseasUser ? '🤝 Connected labs' : '🤝 연결된 업체'}
                        </h2>
                        <p style={styles.sectionSubtitle}>
                            {isOverseasUser
                                ? `You are connected with ${connectedPartners.length} lab${connectedPartners.length === 1 ? '' : 's'}.`
                                : `현재 ${connectedPartners.length}개 업체와 연결되어 있습니다 • 카드를 클릭하면 상세정보를 볼 수 있습니다`}
                        </p>
                    </div>
                    {!isOverseasUser && (
                    <div style={styles.filterGroup}>
                        <Filter size={16} />
                        <select 
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            style={styles.filterSelect}
                        >
                            <option value="all">전체</option>
                            <option value="dental">치과만</option>
                            <option value="lab">기공소만</option>
                            <option value="outsource">외주업체만</option>
                        </select>
                    </div>
                    )}
                </div>
                {connectedPartners.length === 0 ? (
                    <div style={styles.emptyState}>
                        <Users size={64} color="#cbd5e1" />
                        <p style={styles.emptyText}>
                            {filterType === 'all'
                                ? '연결된 업체가 없습니다'
                                : filterType === 'dental'
                                ? '연결된 치과가 없습니다'
                                : filterType === 'outsource'
                                ? '연결된 외주업체가 없습니다'
                                : '연결된 기공소가 없습니다'}
                        </p>
                        <p style={styles.emptySubtext}>위의 검색 기능을 통해 업체를 찾아보세요</p>
                    </div>
                ) : (
                    <div style={styles.grid}>
                        {connectedPartners.map((conn) => (
                            <div 
                                key={conn.id} 
                                style={styles.connectedCard}
                                onClick={() => { if (!isOverseasUser) handleCardClick(conn); }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                                    <div style={styles.connectedBadge}>{isOverseasUser ? '✓ Connected' : '✓ 연결됨'}</div>
                                </div>
                                <h3 style={styles.connectedName}>
                                    {conn.partnerData?.businessName || '이름 없음'}
                                </h3>
                                {!isOverseasUser && (
                                    <p style={styles.connectedEmail}>
                                        <Mail size={14} />
                                        {conn.partnerData?.email}
                                    </p>
                                )}
                                {!isOverseasUser && conn.partnerData?.phone && (
                                    <p style={styles.connectedPhone}>
                                        <Phone size={14} />
                                        {conn.partnerData?.phone}
                                    </p>
                                )}
                                <span style={{
                                    ...styles.connectedType,
                                    backgroundColor: getTypeDisplay(conn.partnerData?.businessType).bg,
                                    color: getTypeDisplay(conn.partnerData?.businessType).color
                                }}>
                                    {isOverseasUser ? 'Dental lab' : getTypeDisplay(conn.partnerData?.businessType).label}
                                </span>
                                <div style={styles.clickHint}>
                                    {isOverseasUser ? 'Use Chat to communicate' : '클릭하여 상세정보 보기 →'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 상세 정보 모달 */}
            {showDetailModal && selectedPartner && (
                <div style={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>업체 상세 정보</h2>
                            <button
                                onClick={() => setShowDetailModal(false)}
                                style={styles.modalCloseButton}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        
                        <div style={styles.modalBody}>
                            {/* 업체명 및 타입 */}
                            <div style={styles.modalSection}>
                                <div style={styles.modalBusinessHeader}>
                                    <h3 style={styles.modalBusinessName}>
                                        {selectedPartner.partnerData?.businessName || '이름 없음'}
                                    </h3>
                                    <span style={{
                                        ...styles.modalTypeBadge,
                                        backgroundColor: getTypeDisplay(selectedPartner.partnerData?.businessType).bg,
                                        color: getTypeDisplay(selectedPartner.partnerData?.businessType).color
                                    }}>
                                        {getTypeDisplay(selectedPartner.partnerData?.businessType).label}
                                    </span>
                                </div>
                            </div>

                            {/* 연락 정보 */}
                            <div style={styles.modalSection}>
                                <h4 style={styles.modalSectionTitle}>연락 정보</h4>
                                
                                <div style={styles.modalDetailItem}>
                                    <Mail size={18} color="#64748b" />
                                    <div style={styles.modalDetailContent}>
                                        <div style={styles.modalDetailLabel}>이메일</div>
                                        <div style={styles.modalDetailValue}>
                                            {selectedPartner.partnerData?.email || '-'}
                                        </div>
                                    </div>
                                </div>

                                {selectedPartner.partnerData?.phone && (
                                    <div style={styles.modalDetailItem}>
                                        <Phone size={18} color="#64748b" />
                                        <div style={styles.modalDetailContent}>
                                            <div style={styles.modalDetailLabel}>전화번호</div>
                                            <div style={styles.modalDetailValue}>
                                                {selectedPartner.partnerData.phone}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 사업장 정보 */}
                            {(selectedPartner.partnerData?.address || selectedPartner.partnerData?.businessNumber) && (
                                <div style={styles.modalSection}>
                                    <h4 style={styles.modalSectionTitle}>사업장 정보</h4>
                                    
                                    {selectedPartner.partnerData?.address && (
                                        <div style={styles.modalDetailItem}>
                                            <MapPin size={18} color="#64748b" />
                                            <div style={styles.modalDetailContent}>
                                                <div style={styles.modalDetailLabel}>주소</div>
                                                <div style={styles.modalDetailValue}>
                                                    {selectedPartner.partnerData.address}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedPartner.partnerData?.businessNumber && (
                                        <div style={styles.modalDetailItem}>
                                            <Building2 size={18} color="#64748b" />
                                            <div style={styles.modalDetailContent}>
                                                <div style={styles.modalDetailLabel}>사업자번호</div>
                                                <div style={styles.modalDetailValue}>
                                                    {selectedPartner.partnerData.businessNumber}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 연결 정보 */}
                            <div style={styles.modalSection}>
                                <h4 style={styles.modalSectionTitle}>연결 정보</h4>
                                
                                <div style={styles.modalDetailItem}>
                                    <Clock size={18} color="#64748b" />
                                    <div style={styles.modalDetailContent}>
                                        <div style={styles.modalDetailLabel}>연결일</div>
                                        <div style={styles.modalDetailValue}>
                                            {formatDate(selectedPartner.updatedAt || selectedPartner.createdAt)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={styles.modalFooter}>
                            <button
                                onClick={() => handleDisconnect(
                                    selectedPartner.id, 
                                    selectedPartner.partnerData?.businessName
                                )}
                                style={styles.modalDisconnectButton}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#dc2626';
                                    e.currentTarget.style.transform = 'scale(1.02)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#ef4444';
                                    e.currentTarget.style.transform = 'scale(1)'}
                                }
                            >
                                <Trash2 size={18} />
                                연결 해제
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

const styles = {
    loading: {
        textAlign: 'center',
        padding: '80px 20px',
        color: '#64748b',
    },
    spinner: {
        width: '50px',
        height: '50px',
        margin: '0 auto 20px',
        border: '3px solid #e2e8f0',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '16px',
        marginBottom: '32px',
    },
    statCard: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    statIcon: {
        width: '56px',
        height: '56px',
        borderRadius: '12px',
        backgroundColor: '#eef2ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    statContent: {
        flex: 1,
    },
    statValue: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#0f172a',
        marginBottom: '4px',
    },
    statLabel: {
        fontSize: '14px',
        color: '#64748b',
        fontWeight: '500',
    },
    searchSection: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #e2e8f0',
        marginBottom: '32px',
    },
    sectionTitle: {
        margin: '0 0 16px 0',
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
    },
    sectionSubtitle: {
        margin: '4px 0 0 0',
        fontSize: '13px',
        color: '#94a3b8',
    },
    searchBox: {
        display: 'flex',
        gap: '12px',
    },
    searchInput: {
        flex: 1,
        padding: '12px 16px',
        border: '2px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        transition: 'all 0.2s',
        outline: 'none',
    },
    searchButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 28px',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
    },
    searchResults: {
        marginTop: '20px',
    },
    staffNotice: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 40px',
        backgroundColor: '#fffbeb',
        borderRadius: '16px',
        border: '2px dashed #fbbf24',
        textAlign: 'center',
        marginTop: '20px',
    },
    staffNoticeTitle: {
        margin: '20px 0 12px 0',
        fontSize: '20px',
        fontWeight: '700',
        color: '#92400e',
    },
    staffNoticeText: {
        margin: 0,
        fontSize: '15px',
        color: '#78350f',
        lineHeight: '1.6',
    },
    searchResultsHeader: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b',
        marginBottom: '12px',
        paddingBottom: '12px',
        borderBottom: '2px solid #f1f5f9',
    },
    resultCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '20px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '2px solid #e2e8f0',
        marginBottom: '12px',
        transition: 'all 0.2s',
    },
    resultInfo: {
        flex: 1,
    },
    resultHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '12px',
    },
    resultName: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '700',
        color: '#0f172a',
    },
    resultDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    resultEmail: {
        margin: 0,
        fontSize: '13px',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    resultType: {
        padding: '4px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '700',
    },
    connectButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 24px',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
    },
    statusBadge: {
        padding: '10px 20px',
        backgroundColor: '#fef3c7',
        color: '#d97706',
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: '700',
    },
    statusBadgeConnected: {
        padding: '10px 20px',
        backgroundColor: '#d1fae5',
        color: '#059669',
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: '700',
    },
    statusBadgeRejected: {
        padding: '10px 20px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: '700',
    },
    section: {
        marginBottom: '32px',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    badge: {
        padding: '6px 14px',
        backgroundColor: '#fef3c7',
        color: '#d97706',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '700',
    },
    filterGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
    },
    filterSelect: {
        padding: '4px 8px',
        border: 'none',
        backgroundColor: 'transparent',
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
        cursor: 'pointer',
        outline: 'none',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '20px',
    },
    requestCard: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '24px',
        border: '2px solid #fef3c7',
        position: 'relative',
        transition: 'all 0.3s',
    },
    // 배지 겹침 수정(2026-08-21): absolute 우상단 고정 → 헤더 줄 안의 일반 배치.
    // 업체유형 배지와 같은 자리에 떠서 글씨가 겹치던 문제 해소.
    requestBadge: {
        padding: '4px 10px',
        backgroundColor: '#fef3c7',
        color: '#d97706',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '700',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '8px',
        marginBottom: '8px',
    },
    cardTitle: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '700',
        color: '#0f172a',
        flex: 1,
        minWidth: 0,
        wordBreak: 'keep-all',
        lineHeight: 1.35,
    },
    cardType: {
        padding: '4px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '700',
        display: 'inline-block',
        marginBottom: '10px',
        whiteSpace: 'nowrap',
    },
    cardInfo: {
        marginBottom: '20px',
    },
    cardEmail: {
        margin: '0 0 8px 0',
        fontSize: '13px',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    cardDate: {
        margin: '0 0 8px 0',
        fontSize: '12px',
        color: '#94a3b8',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    cardActions: {
        display: 'flex',
        gap: '10px',
    },
    acceptButton: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '12px',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    },
    rejectButton: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '12px',
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
    },
    sentPendingNote: {
        padding: '12px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        fontSize: '13px',
        color: '#64748b',
        textAlign: 'center',
        fontWeight: '500',
    },
    emptyState: {
        textAlign: 'center',
        padding: '80px 20px',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        border: '2px dashed #e2e8f0',
    },
    emptyText: {
        marginTop: '20px',
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
        marginBottom: '8px',
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#94a3b8',
    },
    connectedCard: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '24px',
        border: '2px solid #e2e8f0',
        position: 'relative',
        transition: 'all 0.3s',
        textAlign: 'center',
        cursor: 'pointer',
    },
    connectedBadge: {
        padding: '4px 10px',
        backgroundColor: '#d1fae5',
        color: '#059669',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '700',
        whiteSpace: 'nowrap',
    },
    connectedName: {
        margin: '0 0 12px 0',
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
    },
    connectedEmail: {
        margin: '0 0 8px 0',
        fontSize: '13px',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
    },
    connectedPhone: {
        margin: '0 0 16px 0',
        fontSize: '13px',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
    },
    connectedType: {
        padding: '8px 16px',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '700',
        display: 'inline-block',
        marginBottom: '12px',
    },
    clickHint: {
        fontSize: '12px',
        color: '#94a3b8',
        fontWeight: '500',
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #f1f5f9',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
    },
    modalContent: {
        backgroundColor: '#ffffff',
        borderRadius: '20px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '28px',
        borderBottom: '2px solid #f1f5f9',
    },
    modalTitle: {
        margin: 0,
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a',
    },
    modalCloseButton: {
        padding: '8px',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        transition: 'all 0.2s',
    },
    modalBody: {
        padding: '28px',
    },
    modalSection: {
        marginBottom: '28px',
        paddingBottom: '28px',
        borderBottom: '1px solid #f1f5f9',
    },
    modalBusinessHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
    },
    modalBusinessName: {
        margin: 0,
        fontSize: '22px',
        fontWeight: '700',
        color: '#0f172a',
    },
    modalTypeBadge: {
        padding: '8px 16px',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '700',
    },
    modalSectionTitle: {
        margin: '0 0 20px 0',
        fontSize: '16px',
        fontWeight: '700',
        color: '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    modalDetailItem: {
        display: 'flex',
        gap: '16px',
        padding: '16px 0',
        borderBottom: '1px solid #f8fafc',
    },
    modalDetailContent: {
        flex: 1,
    },
    modalDetailLabel: {
        fontSize: '12px',
        color: '#94a3b8',
        marginBottom: '6px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    modalDetailValue: {
        fontSize: '15px',
        color: '#0f172a',
        fontWeight: '500',
        wordBreak: 'break-word',
    },
    modalFooter: {
        padding: '24px 28px',
        borderTop: '2px solid #f1f5f9',
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: '#f8fafc',
        borderBottomLeftRadius: '20px',
        borderBottomRightRadius: '20px',
    },
    modalDisconnectButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 28px',
        backgroundColor: '#ef4444',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
    },
};

export default ConnectionList;